/**
 * TestDefinitionRecipeSection.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B2: the "Advanced —
 * multi-step recipe" collapsed Accordion (D3). Nearly every test's recipe is
 * exactly its primary task type; this expander is the escape hatch for real
 * multi-step recipes.
 *
 * Owns its own recipe fetch (lazy, on first expand) + add/remove mutations:
 *   add    — Autocomplete over all active task types (409 RECIPE_ROW_EXISTS
 *            surfaced politely)
 *   remove — ConfirmDialog; 400 REMOVE_LAST_RECIPE_ROW (the >=1-row invariant)
 *            surfaced politely; liveTaskCount noted (removal only changes
 *            FUTURE generation)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box,
  Chip, CircularProgress, IconButton, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import { Add, DeleteOutline, ExpandMore, Star } from '@mui/icons-material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import {
  addRecipeTaskType,
  fetchRecipe,
  removeRecipeTaskType,
  type TestDefinitionTaskType,
} from '../../../services/testDefinitionService';
import type { Skill, TaskType } from '../../../types/schedule';
import { guidEquals } from './testCatalogHelpers';

interface RecipeSectionProps {
  catalogKey: string;
  /** From the unified payload — shown before the lazy recipe fetch runs. */
  recipeSize: number;
  primaryTaskTypeKey: string | null;
  allTaskTypes: TaskType[];
  skills: Skill[];
  /** Called after any recipe change so the page refreshes the payload. */
  onChanged: () => void;
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: string } } }).response?.data?.error
  ?? (err instanceof Error ? err.message : fallback);

export const TestDefinitionRecipeSection = ({
  catalogKey,
  recipeSize,
  primaryTaskTypeKey,
  allTaskTypes,
  skills,
  onChanged,
}: RecipeSectionProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [recipe, setRecipe] = useState<TestDefinitionTaskType[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);
  const [addValue, setAddValue] = useState<TaskType | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  const loadRecipe = useCallback(async () => {
    setLoading(true);
    try {
      setRecipe(await fetchRecipe(catalogKey));
      setErrorMsg(null);
    } catch {
      setErrorMsg('Failed to load the recipe.');
    } finally {
      setLoading(false);
    }
  }, [catalogKey]);

  // Reset per selected item; fetch lazily on first expand.
  useEffect(() => {
    setRecipe(null);
    setExpanded(false);
    setErrorMsg(null);
    setInfoMsg(null);
    setAddValue(null);
  }, [catalogKey]);

  useEffect(() => {
    if (expanded && recipe === null && !loading) void loadRecipe();
  }, [expanded, recipe, loading, loadRecipe]);

  const skillName = useCallback(
    (skillKey: string | null) =>
      skillKey ? skills.find((s) => guidEquals(s.SkillKey, skillKey))?.Name ?? 'skill' : null,
    [skills],
  );

  /** Task types not already in the recipe (case-insensitive GUID compare). */
  const addOptions = useMemo(() => {
    const inRecipe = new Set((recipe ?? []).map((tt) => tt.TaskTypeKey.toLowerCase()));
    return allTaskTypes.filter((tt) => !inRecipe.has(tt.TaskTypeKey.toLowerCase()));
  }, [allTaskTypes, recipe]);

  const handleAdd = async (taskType: TaskType | null) => {
    setAddValue(null);
    if (!taskType) return;
    setBusy(true);
    setErrorMsg(null);
    setInfoMsg(null);
    try {
      setRecipe(await addRecipeTaskType(catalogKey, taskType.TaskTypeKey));
      onChanged();
    } catch (err) {
      setErrorMsg(apiErrorMessage(err, 'Failed to add the task type to the recipe.'));
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async (taskType: TestDefinitionTaskType) => {
    setBusy(true);
    setErrorMsg(null);
    setInfoMsg(null);
    try {
      const result = await removeRecipeTaskType(catalogKey, taskType.TaskTypeKey);
      if (result.liveTaskCount > 0) {
        setInfoMsg(
          `Removed "${taskType.Name}". ${result.liveTaskCount} already-scheduled task`
          + `${result.liveTaskCount === 1 ? ' keeps' : 's keep'} referencing it — only future `
          + 'schedule generation changes.',
        );
      }
      await loadRecipe();
      onChanged();
    } catch (err) {
      setErrorMsg(apiErrorMessage(err, 'Failed to remove the task type from the recipe.'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = (taskType: TestDefinitionTaskType) => {
    setConfirm({
      open: true,
      title: 'Remove recipe step?',
      message: `Remove "${taskType.Name}" from this test's recipe?\n\nWork-order lines on this `
        + 'test will stop generating this step for FUTURE schedule plans; already-scheduled '
        + 'tasks are untouched.',
      confirmLabel: 'Remove step',
      confirmColor: 'error',
      onConfirm: () => void doRemove(taskType),
    });
  };

  const count = recipe?.length ?? recipeSize;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_e, value) => setExpanded(value)}
      sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        borderRadius: 1.5,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Advanced — multi-step recipe
        </Typography>
        <Chip
          size="small"
          label={`${count} step${count === 1 ? '' : 's'}`}
          sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 600 }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Every work-order line on this test generates ONE schedule task per recipe step.
          Most tests have exactly one step — their matching task type.
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErrorMsg(null)}>{errorMsg}</Alert>
        )}
        {infoMsg && (
          <Alert severity="info" sx={{ mb: 1 }} onClose={() => setInfoMsg(null)}>{infoMsg}</Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {(recipe ?? []).map((tt) => {
          const isPrimary = primaryTaskTypeKey !== null && guidEquals(tt.TaskTypeKey, primaryTaskTypeKey);
          const skill = skillName(tt.SkillKey);
          return (
            <Box
              key={tt.TaskTypeKey}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}
            >
              {isPrimary && (
                <Tooltip title="Primary task type — edited by the form above">
                  <Star sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                </Tooltip>
              )}
              <Typography variant="body2" sx={{ fontWeight: isPrimary ? 700 : 500, fontSize: '0.8rem' }}>
                {tt.Name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tt.DefaultMinutes != null ? `${tt.DefaultMinutes} min` : 'no minutes'}
              </Typography>
              {skill && (
                <Chip
                  size="small"
                  label={skill}
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    backgroundColor: alpha(theme.schedule.employeeAlloc, 0.12),
                    color: theme.schedule.employeeAlloc,
                    fontWeight: 600,
                  }}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemove(tt)}
                disabled={busy}
                title="Remove from recipe"
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          );
        })}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Add fontSize="small" sx={{ color: 'text.disabled' }} />
          <Autocomplete
            size="small"
            fullWidth
            options={addOptions}
            value={addValue}
            getOptionLabel={(tt) => tt.Name}
            groupBy={(tt) => tt.Category ?? 'No category'}
            isOptionEqualToValue={(o, v) => guidEquals(o.TaskTypeKey, v.TaskTypeKey)}
            onChange={(_e, value) => void handleAdd(value)}
            disabled={busy || recipe === null}
            renderInput={(params) => (
              <TextField {...params} placeholder="Add a task type to the recipe…" />
            )}
          />
        </Box>
      </AccordionDetails>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(CONFIRM_INITIAL)} />
    </Accordion>
  );
};
